import * as firebase from "firebase/app";
import "firebase/auth";
import "firebase/firestore";
import mime from "mime-types";
import { GeoFirestore } from "geofirestore";
import User from "./../models/user.model";
import constants from "../constants";
import { getFileMetadata } from "../utils/fileInspection";

if (firebase.apps && firebase.apps.length === 0) {
  firebase.initializeApp(constants.firebaseConfig);
}

const PROFILE_COLLECTION_NAME = constants.collections.profiles;

export default class AuthService {
  static addProfile(data) {
    return new Promise((resolve, reject) => {
      let db;
      const { uid, ...profile } = data;
      profile.createdAt = firebase.firestore.FieldValue.serverTimestamp();
      if (profile.lat && profile.long) {
        db = new GeoFirestore(firebase.firestore());
        profile.coordinates = new firebase.firestore.GeoPoint(
          profile.lat,
          profile.long
        );
      } else {
        db = firebase.firestore();
      }
      db.collection(PROFILE_COLLECTION_NAME)
        .doc(uid)
        .set(profile)
        .then(() => {
          resolve();
        })
        .catch(reject);
    });
  }

  static async addProfileAsync(data) {
    let db;
    const { uid, imageUrl, ...profile } = data;
    profile.createdAt = firebase.firestore.FieldValue.serverTimestamp();

    if (profile.lat && profile.long) {
      db = new GeoFirestore(firebase.firestore());
      profile.coordinates = new firebase.firestore.GeoPoint(
        profile.lat,
        profile.long
      );
      if (imageUrl && (imageUrl instanceof File || imageUrl instanceof Blob)) {
        const pictureMetaData = await getFileMetadata(imageUrl);
        const storageRef = firebase.storage().ref();
        const path = `profiles/community/${uid}/picture.${mime.extension(
          pictureMetaData.binaryFileType
        )}`;
        profile.picture = path;
        const pictureRef = storageRef.child(path);
        const pictureSnapshot = pictureRef.put(imageUrl, {
          contentType: pictureMetaData.binaryFileType,
          owner: `${uid}`
        });
        await pictureSnapshot;
      }
    } else {
      db = firebase.firestore();
    }
    await db
      .collection(PROFILE_COLLECTION_NAME)
      .doc(uid)
      .set(profile);
  }

  static getProfile(uid) {
    return new Promise((resolve, reject) => {
      const db = firebase.firestore();
      db.collection(PROFILE_COLLECTION_NAME)
        .doc(uid)
        .get()
        .then(doc => {
          if (doc.exists) {
            const document = doc.data();
            if (document.d) {
              resolve(document.d);
            } else {
              resolve(document);
            }
          } else {
            reject(new Error(`Profile ${uid} does not exist!`));
          }
        })
        .catch(reject);
    });
  }

  static createUser(userData) {
    const { email, password, ...profile } = userData;
    return new Promise((resolve, reject) => {
      let forCreatingUsers;
      if (firebase.apps && firebase.apps.length === 1) {
        forCreatingUsers = firebase.initializeApp(
          constants.firebaseConfig,
          "Secondary"
        );
      } else {
        forCreatingUsers = firebase.app("Secondary");
      }
      const creator = firebase.auth().currentUser;
      if (creator) {
        const db = forCreatingUsers.firestore();
        forCreatingUsers
          .auth()
          .createUserWithEmailAndPassword(email, password)
          .then(firebaseUser => {
            profile.uid = firebaseUser.user.uid;
            profile.email = email;
            profile.createdBy = db
              .collection(constants.collections.profiles)
              .doc(creator.uid);
            const getTheProfile = () => {
              AuthService.getProfile(profile.uid)
                .then(profileDoc => {
                  resolve(new User(firebaseUser.user, profileDoc).toSubClass());
                })
                .catch(reject);
            };
            if (profile.lat && profile.long) {
              AuthService.addProfileAsync(profile)
                .then(() => {
                  getTheProfile();
                })
                .catch(reject);
            } else {
              AuthService.addProfile(profile)
                .then(() => {
                  getTheProfile();
                })
                .catch(reject);
            }
          })
          .catch(error => {
            reject(error);
          });
      } else {
        reject(new Error(`Cannot creat user if there's not signed in.`));
      }
    });
  }

  static login(email, password) {
    return new Promise((resolve, reject) => {
      firebase
        .auth()
        .signInWithEmailAndPassword(email, password)
        .then(data => {
          AuthService.getProfile(data.user.uid)
            .then(profileDoc => {
              const user = new User(data.user, profileDoc).toSubClass();
              resolve(user);
            })
            .catch(reject);
        })
        .catch(reject);
    });
  }

  static logout() {
    return new Promise((resolve, reject) => {
      firebase
        .auth()
        .signOut()
        .then(data => resolve(data))
        .catch(error => reject(error));
    });
  }

  static async getCurrentUser() {
    const authUser = firebase.auth().currentUser;
    const profile = await AuthService.getProfile(authUser.uid);
    return new User(authUser, profile).toSubClass();
  }
}
